import { useState, useEffect, useCallback, useRef } from "react";

const API = "/api/v1";
const ORANGE = "#E07820";
const ORANGE_LIGHT = "#FDF2E8";

function getUserSaleType() {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) return "BOTH";
    const payload = JSON.parse(atob(token.split(".")[1]));
    const role = (payload.role || "").toLowerCase();
    if (role.includes("export")) return "EXPORT";
    if (role.includes("local") || role.includes("domestic")) return "LOCAL";
    return "BOTH";
  } catch { return "BOTH"; }
}

function Toast({ toast }) {
  if (!toast) return null;
  const bg = toast.type === "error" ? "#ef4444" : "#22c55e";
  return (
    <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, background: bg, color: "#fff", padding: "12px 20px", borderRadius: 8, fontSize: 13, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", maxWidth: 340 }}>
      {toast.msg}
    </div>
  );
}

function SendModal({ count, onClose, onSend, sending }) {
  const [channels, setChannels] = useState({ email: true, whatsapp: false });
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 28, width: 400, boxShadow: "0 8px 40px rgba(0,0,0,0.2)" }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Send to {count} client{count > 1 ? "s" : ""}</h3>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#888" }}>Choose channels:</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
          {[{ key: "email", icon: "📧", label: "Email", sub: "PDF stock list" }, { key: "whatsapp", icon: "💬", label: "WhatsApp", sub: "Interakt template" }].map(({ key, icon, label, sub }) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", border: `2px solid ${channels[key] ? ORANGE : "#e5e5e5"}`, borderRadius: 8, cursor: "pointer", background: channels[key] ? ORANGE_LIGHT : "#fafafa" }}>
              <input type="checkbox" checked={channels[key]} onChange={() => setChannels(p => ({ ...p, [key]: !p[key] }))} style={{ width: 16, height: 16, accentColor: ORANGE }} />
              <span style={{ fontSize: 16 }}>{icon}</span>
              <div><div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div><div style={{ fontSize: 11, color: "#888" }}>{sub}</div></div>
            </label>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "10px", border: "1px solid #ddd", borderRadius: 7, background: "#f5f5f5", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={() => onSend(channels)} disabled={sending || (!channels.email && !channels.whatsapp)}
            style={{ flex: 2, padding: "10px", border: "none", borderRadius: 7, background: ORANGE, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            {sending ? "Sending..." : `Send to ${count} Client${count > 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadZone({ onUpload, uploading, saleType }) {
  const [drag, setDrag] = useState(false);
  const ref = useRef();
  const handleDrop = (e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) onUpload(f); };
  return (
    <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={handleDrop} onClick={() => ref.current.click()}
      style={{ border: `2px dashed ${drag ? ORANGE : "#ddd"}`, borderRadius: 8, padding: "10px 18px", cursor: "pointer", background: drag ? ORANGE_LIGHT : "#fafafa", textAlign: "center" }}>
      <input ref={ref} type="file" accept=".xlsx" style={{ display: "none" }} onChange={(e) => onUpload(e.target.files[0])} />
      {uploading
        ? <p style={{ margin: 0, color: ORANGE, fontSize: 12, fontWeight: 600 }}>Uploading...</p>
        : <><p style={{ margin: 0, fontWeight: 600, fontSize: 12, color: ORANGE }}>Upload {saleType} Stock</p><p style={{ margin: "2px 0 0", fontSize: 11, color: "#aaa" }}>Drop .xlsx here</p></>
      }
    </div>
  );
}

export default function StockPage() {
  const userSaleType = getUserSaleType();
  const isAdmin = userSaleType === "BOTH";
  const [activeType, setActiveType] = useState(userSaleType === "LOCAL" ? "LOCAL" : "EXPORT");
  const [subTab, setSubTab] = useState("matched");
  const activeTypeRef = useRef("");
  const [matches, setMatches] = useState([]);
  const [waStatus, setWaStatus] = useState({});
  const [unmatched, setUnmatched] = useState([]);
  const [stats, setStats] = useState({ weekLabel: "", stockCount: 0, matchCount: 0, unmatchedCount: 0 });
  const [selected, setSelected] = useState(new Set());
  const [activeClient, setActiveClient] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };
  const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem("access_token")}` });

  const fetchData = useCallback(async (saleType) => {
    setLoading(true); setActiveClient(null); setSelected(new Set());
    activeTypeRef.current = saleType;
    try {
      const [mRes, uRes] = await Promise.all([
        fetch(`${API}/stock/matches?sale_type=${saleType}`, { headers: hdrs() }),
        fetch(`${API}/stock/unmatched?sale_type=${saleType}`, { headers: hdrs() }),
      ]);
      const mData = await mRes.json(); const uData = await uRes.json();
      if (activeTypeRef.current !== saleType) return;
      setMatches(mData.matches || []); setUnmatched(uData.unmatched || []);
      try {
        const wr = await fetch(`${API}/stock/whatsapp-status?sale_type=${saleType}`, { headers: hdrs() });
        const wd = await wr.json();
        if (activeTypeRef.current !== saleType) return;
        setWaStatus(wd.statuses || {});
      } catch { /* status is optional - never block the list */ }
      setStats({ weekLabel: mData.week_label || "", stockCount: mData.stock_count || 0, matchCount: mData.match_count || 0, unmatchedCount: uData.count || 0 });
    } catch { showToast("Failed to load", "error"); }
    setLoading(false);
  }, []);

  const [uploadHistory, setUploadHistory] = useState([]);
  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/stock/upload-history`, { headers: hdrs() });
      const data = await res.json();
      setUploadHistory(data.history || []);
    } catch { showToast("Failed to load history", "error"); }
  }, []);

  useEffect(() => { fetchData(activeType); }, [activeType, fetchData]);
  useEffect(() => { if (subTab === "history") loadHistory(); }, [subTab, loadHistory]);

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const res = await fetch(`${API}/stock/upload`, { method: "POST", headers: hdrs(), body: fd });
      const data = await res.json();
      if (res.ok) { showToast(`Uploaded ${data.total} items (Export: ${data.export}, Local: ${data.local})`); fetchData(activeType); }
      else showToast(data.detail || "Upload failed", "error");
    } catch { showToast("Upload error", "error"); }
    setUploading(false);
  };

  const toggleSelect = (id) => { setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; }); };
  const toggleAll = () => { const list = subTab === "matched" ? matches : unmatched; setSelected(prev => prev.size === list.length ? new Set() : new Set(list.map(m => m.account_id))); };

  const handleSend = async (channels) => {
    setSending(true);
    const ids = Array.from(selected);
    try {
      const promises = [];
      if (channels.email) promises.push(fetch(`${API}/stock/email/bulk?sale_type=${activeType}`, { method: "POST", headers: { ...hdrs(), "Content-Type": "application/json" }, body: JSON.stringify(ids) }).then(r => r.json()).then(d => ({ channel: "email", ...d })));
      if (channels.whatsapp) promises.push(fetch(`${API}/stock/whatsapp/bulk?sale_type=${activeType}`, { method: "POST", headers: { ...hdrs(), "Content-Type": "application/json" }, body: JSON.stringify(ids) }).then(r => r.json()).then(d => ({ channel: "whatsapp", ...d })));
      const results = await Promise.all(promises);
      const emailRes = results.find(r => r.channel === "email");
      const emailSent = emailRes?.sent || 0;
      const emailSkipped = emailRes ? (emailRes.results || []).filter(r => r.status === "skipped").length : 0;
      const emailInvalid = emailRes ? (emailRes.results || []).filter(r => r.status === "error").length : 0;
      const waSent = results.find(r => r.channel === "whatsapp")?.sent || 0;
      const parts = []; if (channels.email) parts.push(`${emailSent} emails`); if (channels.whatsapp) parts.push(`${waSent} WhatsApp`);
      const extras = [];
      if (emailSkipped > 0) extras.push(`${emailSkipped} already sent this week`);
      if (emailInvalid > 0) extras.push(`${emailInvalid} invalid email`);
      showToast(`Sent: ${parts.join(" + ")}${extras.length ? " · Skipped: " + extras.join(", ") : ""}`);
      setSelected(new Set());
    } catch { showToast("Send failed", "error"); }
    setSending(false); setShowSendModal(false);
  };

  const { weekLabel, stockCount, matchCount, unmatchedCount } = stats;
  const list = subTab === "matched" ? matches : unmatched;
  const filtered = search.trim() ? list.filter(m => m.company.toLowerCase().includes(search.toLowerCase()) || (m.owner_name || "").toLowerCase().includes(search.toLowerCase())) : list;
  const displayClient = activeClient || (filtered.length > 0 ? filtered[0] : null);

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", fontFamily: "Inter, sans-serif", background: "#f5f5f5" }}>
      <Toast toast={toast} />
      {showSendModal && <SendModal count={selected.size} onClose={() => setShowSendModal(false)} onSend={handleSend} sending={sending} />}
      <div style={{ background: "#fff", borderBottom: "1px solid #e8e8e8", padding: "12px 24px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1a1a1a" }}>Stock Matching</h1>
          {weekLabel && <p style={{ margin: "2px 0 0", fontSize: 12, color: "#888" }}>{weekLabel} · {stockCount} items · <span style={{ color: "#22c55e", fontWeight: 600 }}>{matchCount} matched</span></p>}
        </div>
        {isAdmin && (
          <div style={{ display: "flex" }}>
            {[{ key: "EXPORT", label: "Export" }, { key: "LOCAL", label: "Local" }].map(({ key, label }, i) => (
              <button key={key} onClick={() => { setActiveType(key); setSubTab("matched"); setSearch(""); }}
                style={{ padding: "7px 20px", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, borderRadius: i === 0 ? "6px 0 0 6px" : "0 6px 6px 0", background: activeType === key ? ORANGE : "#f0f0f0", color: activeType === key ? "#fff" : "#777" }}>
                {label}
              </button>
            ))}
          </div>
        )}
        {!isAdmin && <span style={{ fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 20, background: activeType === "EXPORT" ? "#dbeafe" : "#dcfce7", color: activeType === "EXPORT" ? "#1d4ed8" : "#16a34a" }}>{activeType}</span>}
        <UploadZone onUpload={uploadFile} uploading={uploading} saleType={activeType} />
        <button onClick={async () => {
          if (!window.confirm("Fetch latest stock from Renu's email and load it?")) return;
          try {
            const res = await fetch(`${API}/stock/fetch-from-email?dry_run=false`, { method: "POST", headers: hdrs() });
            const data = await res.json();
            if (res.ok) { alert("Stock fetched and loaded!"); fetchData(activeType); }
            else { alert("Fetch failed: " + (data.detail || "error")); }
          } catch (e) { alert("Fetch error: " + e.message); }
        }} style={{ padding: "7px 16px", background: ORANGE, color: "#fff", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Fetch</button>
        <button onClick={async () => {
          try {
            const res = await fetch(`${API}/stock/download`, { headers: hdrs() });
            if (!res.ok) { alert("Download failed"); return; }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = "Stock.xlsx";
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
          } catch (e) { alert("Download error: " + e.message); }
        }} style={{ padding: "7px 16px", background: "#fff", color: "#333", border: "1px solid #ddd", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Download</button>
      </div>
      <div style={{ background: "#fff", borderBottom: "1px solid #e8e8e8", padding: "0 24px", display: "flex" }}>
        {[{ key: "matched", label: `Matched (${matchCount})` }, { key: "history", label: "History" }].map(st => (
          <button key={st.key} onClick={() => { setSubTab(st.key); setSearch(""); setActiveClient(null); setSelected(new Set()); }}
            style={{ padding: "10px 20px", border: "none", borderBottom: subTab === st.key ? `2.5px solid ${ORANGE}` : "2.5px solid transparent", background: "transparent", cursor: "pointer", fontWeight: subTab === st.key ? 700 : 400, fontSize: 13, color: subTab === st.key ? ORANGE : "#777", marginBottom: -1 }}>
            {st.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <div style={{ width: 320, background: "#fff", borderRight: "1px solid #e8e8e8", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "10px 12px", borderBottom: "1px solid #eee", display: "flex", flexDirection: "column", gap: 8 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search client..."
              style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", border: "1px solid #e0e0e0", borderRadius: 6, fontSize: 12, outline: "none" }} />
            {subTab === "matched" && filtered.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#666", cursor: "pointer" }}>
                  <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll} style={{ accentColor: ORANGE }} />
                  {selected.size > 0 ? `${selected.size} selected` : "Select all"}
                </label>
                {selected.size > 0 && <button onClick={() => setShowSendModal(true)} style={{ padding: "5px 12px", background: ORANGE, color: "#fff", border: "none", borderRadius: 5, fontWeight: 700, fontSize: 11, cursor: "pointer" }}>Send ${selected.size}</button>}
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading ? <div style={{ padding: 40, textAlign: "center", color: "#aaa" }}>Loading...</div>
            : filtered.length === 0 ? <div style={{ padding: 40, textAlign: "center", color: "#aaa" }}><p style={{ fontSize: 13 }}>{stockCount === 0 ? "Upload stock to start" : "No results"}</p></div>
            : filtered.map(m => {
              const isActive = displayClient?.account_id === m.account_id;
              const isChecked = selected.has(m.account_id);
              return (
                <div key={m.account_id} onClick={() => setActiveClient(m)}
                  style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0", cursor: "pointer", background: isActive ? ORANGE_LIGHT : "#fff", borderLeft: isActive ? `3px solid ${ORANGE}` : "3px solid transparent" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {subTab === "matched" && <input type="checkbox" checked={isChecked} onChange={(e) => { e.stopPropagation(); toggleSelect(m.account_id); }} onClick={e => e.stopPropagation()} style={{ accentColor: ORANGE, flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.company}</p>
                      <p style={{ margin: "2px 0 0", fontSize: 11, color: "#888" }}>{m.owner_name ? `SC: ${m.owner_name}` : ""}{m.contact_name ? ` · ${m.contact_name}` : ""}</p>
                      {(() => {
                        const w = waStatus[String(m.account_id)];
                        if (!w) return null;
                        const map = { sent: ["Sent", "#888"], delivered: ["Delivered", "#0a7"], read: ["Read", "#0a7"], replied: ["Replied \u2713", "#e07820"] };
                        const [txt, col] = map[w.status] || [w.status, "#888"];
                        return <p style={{ margin: "2px 0 0", fontSize: 10, fontWeight: 600, color: col }}>WhatsApp: {txt}</p>;
                      })()}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 12, background: subTab === "matched" ? "#fff3e8" : "#fee2e2", color: subTab === "matched" ? ORANGE : "#ef4444", flexShrink: 0 }}>{m.items.length}{subTab === "matched" ? " ✓" : " ✗"}</span>
                  </div>
                  <div style={{ marginTop: 5, display: "flex", flexWrap: "wrap", gap: 3 }}>
                    {m.items.slice(0, 3).map((item, i) => <span key={i} style={{ fontSize: 10, padding: "1px 5px", background: "#f0f0f0", borderRadius: 3, color: "#555" }}>{item.grade} {item.size_mm}</span>)}
                    {m.items.length > 3 && <span style={{ fontSize: 10, color: "#aaa" }}>+${m.items.length - 3}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {subTab === "history" ? (
            <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e8e8e8", overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #eee" }}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Upload History <span style={{ fontWeight: 400, fontSize: 12, color: "#888" }}>{uploadHistory.length} uploads</span></h3>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ background: "#f8f8f8" }}>
                    {["Uploaded","Uploader","Types","Total","LOCAL","EXPORT","File"].map(h => <th key={h} style={{ padding: "9px 14px", textAlign: "left", color: "#555", fontWeight: 600, fontSize: 12, borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {uploadHistory.length === 0 ? (
                      <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: "#aaa" }}>No upload history yet.</td></tr>
                    ) : uploadHistory.map((u, idx) => (
                      <tr key={u.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "10px 14px", color: "#333", whiteSpace: "nowrap" }}>{u.uploaded_at ? new Date(u.uploaded_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                        <td style={{ padding: "10px 14px", fontWeight: 600, color: "#1a1a1a" }}>{u.uploader_name || u.uploader_ecode || "—"}</td>
                        <td style={{ padding: "10px 14px" }}><span style={{ fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 12, background: "#fff3e8", color: ORANGE }}>{u.sale_types || "—"}</span></td>
                        <td style={{ padding: "10px 14px", fontWeight: 700, color: "#1a1a1a" }}>{u.total_rows || 0}</td>
                        <td style={{ padding: "10px 14px", color: "#16a34a", fontWeight: 600 }}>{u.local_rows || 0}</td>
                        <td style={{ padding: "10px 14px", color: "#1d4ed8", fontWeight: 600 }}>{u.export_rows || 0}</td>
                        <td style={{ padding: "10px 14px", color: "#666", fontSize: 12, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={u.file_name}>{u.file_name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : !displayClient ? (
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#ccc" }}>
              <div style={{ textAlign: "center" }}><p style={{ fontSize: 40, margin: 0 }}>👈</p><p style={{ fontSize: 14, marginTop: 8 }}>Select a client</p></div>
            </div>
          ) : (
            <>
              <div style={{ background: "#fff", borderRadius: 10, padding: "16px 20px", marginBottom: 16, border: "1px solid #e8e8e8", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1a1a1a" }}>{displayClient.company}</h2>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: "#888" }}>
                    {displayClient.owner_name && `SC: ${displayClient.owner_name}`}{displayClient.contact_name && ` · ${displayClient.contact_name}`}{displayClient.contact_email && ` · ${displayClient.contact_email}`}
                  </p>
                </div>
                {subTab === "matched" && <button onClick={() => { setSelected(new Set([displayClient.account_id])); setShowSendModal(true); }} style={{ padding: "8px 18px", background: ORANGE, color: "#fff", border: "none", borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Send to this Client</button>}
              </div>
              <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e8e8e8", overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", borderBottom: "1px solid #eee" }}>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{subTab === "matched" ? "Matched Stock Items" : "Unmatched Enquiries"} <span style={{ fontWeight: 400, fontSize: 12, color: "#888" }}>{displayClient.items.length} items</span></h3>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr style={{ background: "#f8f8f8" }}>
                      {subTab === "matched"
                        ? ["Grade","Size (mm)","Enq. Qty","Enq. Length","Stock Qty (kg)","Tolerance","HT","Length (mm)"].map(h => <th key={h} style={{ padding: "9px 14px", textAlign: "left", color: "#555", fontWeight: 600, fontSize: 12, borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>{h}</th>)
                        : ["Grade","Size (mm)","Enquiry Qty"].map(h => <th key={h} style={{ padding: "9px 14px", textAlign: "left", color: "#555", fontWeight: 600, fontSize: 12, borderBottom: "1px solid #eee" }}>{h}</th>)
                      }
                    </tr></thead>
                    <tbody>
                      {displayClient.items.map((item, idx) => (
                        <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "10px 14px", fontWeight: 700, color: subTab === "matched" ? ORANGE : "#ef4444" }}>{item.grade}</td>
                          <td style={{ padding: "10px 14px", color: "#333" }}>{item.size_mm}</td>
                          {subTab === "matched" ? <>
                            <td style={{ padding: "10px 14px", color: "#666" }}>{item.enq_qty || "—"}</td>
                            <td style={{ padding: "10px 14px", color: "#666" }}>{item.enq_length || "—"}</td>
                            <td style={{ padding: "10px 14px" }}><span style={{ fontWeight: 700, color: "#16a34a" }}>{item.stock_qty} kg</span>{item.peeling_required && <span style={{ marginLeft: 6, fontSize: 11, background: "#fef3c7", color: "#d97706", padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>Peel {item.peel_amount}mm</span>}</td>
                            <td style={{ padding: "10px 14px", color: "#666" }}>{item.stock_tol || "—"}</td>
                            <td style={{ padding: "10px 14px", color: "#666" }}>{item.stock_ht || "—"}</td>
                            <td style={{ padding: "10px 14px", color: "#666" }}>{item.stock_length || "—"}</td>
                          </> : <td style={{ padding: "10px 14px", color: "#666" }}>{item.qty || "—"}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}